import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import SEO from "../components/common/SEO";
import BlogCTA from "../components/blog/BlogCTA";
import RelatedPosts from "../components/blog/RelatedPosts";
import { getPostBySlug, blogPosts } from "../data/blogPosts";
import { blogPostSchema } from "../utils/blogPostSchema";
import { blogPostAlternates, blogPostUrl, isSpanishPost } from "../utils/blogLocale";
import styles from "./BlogPost.module.css";

/* Each post's HTML body is its own chunk (scripts/build-blog.cjs), so a
   post page downloads its own body and none of the others. The header,
   cover and head tags render from the metadata at once; the body follows
   when its chunk arrives — on a cold landing the static HTML has already
   preloaded it (scripts/prerender.cjs). Bodies stay cached for the
   session, so going back to a post renders it in one pass. */
const bodyModules = import.meta.glob("../data/blogContent/*.js", {
  import: "default",
});
const bodies = new Map();

function usePostBody(slug) {
  const [failed, setFailed] = useState(null);
  const [, setLoaded] = useState(null);
  const load = bodyModules[`../data/blogContent/${slug}.js`];
  useEffect(() => {
    if (!load || bodies.has(slug)) return;
    load().then(
      (html) => {
        bodies.set(slug, html);
        setLoaded(slug);
      },
      (error) => {
        /* A tab opened before a deploy asks for a body the new deploy no
           longer has. index.jsx already reloads once for that. */
        const event = new Event("vite:preloadError", { cancelable: true });
        event.payload = error;
        window.dispatchEvent(event);
        if (!event.defaultPrevented) setFailed(slug);
      },
    );
  }, [slug, load]);
  return { html: bodies.get(slug), failed: !load || failed === slug };
}

/* One URL per post: /es/blog/es-foo or /blog/foo. The other two spellings
   resolve to a post but are not its address; vercel.json 301s them on a
   full load and this does the same for a navigation inside the app.
   Switching an English-only post to ES lands on the Spanish index
   rather than a 404. Returns null when the URL is already right. */
function postRedirect(slug, urlSpanish) {
  if (!slug) return null;
  const english = slug.replace(/^es-/, "");
  const spanish = `es-${english}`;
  const has = (s) => Boolean(getPostBySlug(s));
  if (urlSpanish) {
    if (has(spanish)) return slug === spanish ? null : `/es/blog/${spanish}`;
    if (slug === english) return has(english) ? `/blog/${english}` : null;
    return has(english) ? "/es/blog" : null;
  }
  if (slug === spanish) {
    if (has(spanish)) return `/es/blog/${spanish}`;
    if (has(english)) return `/blog/${english}`;
  }
  return null;
}

/* The contents rail marks the section being read: the last h2 whose top
   has passed the upper third of the viewport. */
function useActiveSection(ids) {
  const [active, setActive] = useState(null);
  useEffect(() => {
    const headings = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!headings.length) return undefined;
    const onScroll = () => {
      const line = Math.min(window.innerHeight * 0.35, 320);
      let current = headings[0].id;
      for (const h of headings) {
        if (h.getBoundingClientRect().top < line) current = h.id;
        else break;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [ids]);
  return active;
}

export default function BlogPost() {
  const { t, i18n } = useTranslation("blog");
  const { t: st } = useTranslation("studio");
  const prefix = i18n.language?.startsWith("es") ? "/es" : "";
  const { slug } = useParams();
  const { pathname } = useLocation();
  const redirect = postRedirect(slug, pathname.startsWith("/es/"));
  const post = redirect ? null : getPostBySlug(slug);
  const body = usePostBody(post?.slug);
  const sections = useMemo(() => {
    const headings = [];
    const html = (body.html || "").replace(
      /<h2([^>]*)>([\s\S]*?)<\/h2>/g,
      (_, attrs, title) => {
        const existing = attrs.match(/\bid=["']([^"']+)["']/);
        const id = existing?.[1] || `article-section-${headings.length + 1}`;
        headings.push({
          id,
          title: title
            .replace(/<[^>]+>/g, "")
            .replace(/&amp;/g, "&")
            .replace(/&quot;/g, '"'),
        });
        return `<h2${attrs}${existing ? "" : ` id="${id}"`}>${title}</h2>`;
      },
    );
    return { headings, html };
  }, [body.html]);
  const headingIds = useMemo(
    () => sections.headings.map((h) => h.id),
    [sections.headings],
  );
  const activeSection = useActiveSection(headingIds);
  if (redirect) return <Navigate to={redirect} replace />;
  if (!post)
    return (
      <section className={styles.notFound}>
        <SEO title={t("notFound.title")} noindex />
        <h1>{t("notFound.title")}</h1>
        <p>{t("notFound.message")}</p>
        <Link to={`${prefix}/blog`}>{t("backToBlog")}</Link>
      </section>
    );
  /* From the post's own language, not the prefix it was reached under:
     /blog/es-foo renders the Spanish post, whose one URL is
     /es/blog/es-foo. Matches the static HTML scripts/prerender.cjs
     writes for both paths. */
  const url = blogPostUrl(post.slug);
  /* Three related reads on every post: same category first, then posts
     sharing a tag, then the newest. UX and SaaS have too few posts to
     fill the row on category alone. */
  const sameLanguage = blogPosts.filter(
    (p) =>
      p.id !== post.id &&
      p.slug.startsWith("es-") === post.slug.startsWith("es-"),
  );
  const sharedTags = (p) => p.tags.filter((tag) => post.tags.includes(tag)).length;
  const related = [
    ...sameLanguage.filter((p) => p.category === post.category),
    ...sameLanguage
      .filter((p) => p.category !== post.category && sharedTags(p) > 0)
      .sort((a, b) => sharedTags(b) - sharedTags(a)),
    ...sameLanguage,
  ]
    .filter((p, i, list) => list.indexOf(p) === i)
    .slice(0, 3);
  const postLang = isSpanishPost(post.slug) ? "es" : "en";
  const postPrefix = postLang === "es" ? "/es" : "";
  const schema = blogPostSchema(post, {
    url,
    lang: postLang,
    breadcrumbs: [
      { name: "JAMAK", url: `https://jamaklab.com${postPrefix}/` },
      { name: "Blog", url: `https://jamaklab.com${postPrefix}/blog` },
      { name: post.title, url },
    ],
  });
  return (
    <>
      <SEO
        title={`${post.title} | JAMAK`}
        description={post.excerpt}
        keywords={(post.seoKeywords || post.tags).join(", ")}
        image={post.featuredImage}
        url={post.canonical || url}
        languageAlternates={
          post.canonical
            ? []
            : blogPostAlternates(
                post.slug,
                blogPosts.map((p) => p.slug),
              )
        }
        type="article"
        article={{
          author: post.author.name,
          publishedTime: post.publishedAt,
          modifiedTime: post.modifiedAt || post.publishedAt,
          section: post.category,
          tags: post.tags,
        }}
        structuredData={schema}
      />
      <article className={styles.article}>
        <header className={styles.hero}>
          <Link to={`${prefix}/blog`} className={styles.back}>
            <ArrowLeft size={16} />
            {t("backToBlog")}
          </Link>
          <h1>{post.title}</h1>
          <div className={styles.heroBottom}>
            <p className={styles.excerpt}>{post.excerpt}</p>
            <div className={styles.author}>
              <img src={post.author.avatar} alt="" width="48" height="48" />
              <div>
                <strong>{post.author.name}</strong>
                <span>{post.author.role}</span>
                {post.author.twitter && (
                  <a
                    href={`https://twitter.com/${post.author.twitter.replace("@", "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {post.author.twitter}
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className={styles.meta}>
            <span>{post.category}</span>
            <time dateTime={post.publishedAt}>
              {new Date(post.publishedAt).toLocaleDateString(
                prefix ? "es-ES" : "en-US",
                { year: "numeric", month: "long", day: "numeric" },
              )}
            </time>
            <span>{post.readingTime}</span>
          </div>
        </header>
        <figure className={styles.cover}>
          <img src={post.featuredImage} alt="" width="1600" height="1000" />
          <figcaption>{st("editorial.study")}</figcaption>
        </figure>
        <div className={styles.reading}>
          <aside className={styles.contents}>
            {sections.headings.length > 0 && (
              <nav aria-label={st("journal.contents")}>
                <p>{st("journal.contents")}</p>
                <ol>
                  {sections.headings.map((h) => (
                    <li key={h.id}>
                      <a
                        href={`#${h.id}`}
                        aria-current={
                          activeSection === h.id ? "location" : undefined
                        }
                      >
                        {h.title}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            )}
          </aside>
          <div className={styles.contentColumn}>
            {sections.headings.length > 0 && (
              <details className={styles.contentsMobile}>
                <summary>
                  {st("journal.contents")}
                  <span>{sections.headings.length}</span>
                </summary>
                <ol>
                  {sections.headings.map((h) => (
                    <li key={h.id}>
                      <a
                        href={`#${h.id}`}
                        onClick={(e) =>
                          e.currentTarget.closest("details").removeAttribute("open")
                        }
                      >
                        {h.title}
                      </a>
                    </li>
                  ))}
                </ol>
              </details>
            )}
            {body.html !== undefined ? (
              <div
                className={styles.prose}
                dangerouslySetInnerHTML={{ __html: sections.html }}
              />
            ) : (
              /* Holds the body's place so the tags, share links and
                 everything after them stay below the fold until it lands. */
              <div
                className={body.failed ? undefined : styles.bodyPending}
                aria-busy={!body.failed}
              />
            )}
            <section className={styles.tags}>
              <h2>{t("topics")}</h2>
              <div>
                {post.tags.map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
              </div>
            </section>
            <section className={styles.share}>
              <h2>{t("shareArticle")}</h2>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(url)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("twitter")}
                <ArrowUpRight size={15} />
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("linkedin")}
                <ArrowUpRight size={15} />
              </a>
            </section>
          </div>
        </div>
      </article>
      <RelatedPosts posts={related} langPrefix={prefix} />
      <BlogCTA />
    </>
  );
}
